// Shared cross-thread distribution inventory: every segment's distribution runs
// joined with their audience / segment / destination context. Runs are derived
// via the segment lifecycle (lifecycleFor). Lives in hooks/ so the monitoring
// screens (Distributions, Destinations) consume one read instead of importing
// thread/segment as a feature edge — same pattern as useAudienceInventory.

import { useMemo } from 'react'
import { useGetThreads } from '@/features/thread/queries'
import type { AudienceConfiguration, Dataset, DatasetType } from '@/lib/domain'
import {
    defaultDestinationRoute,
    findDestination,
    pendingConfig,
} from '@/lib/marketplaceDestinations'
import type {
    DestinationRoute,
    SavedConfig,
} from '@/lib/marketplaceDestinations'
import { segmentKey } from '@/features/segment/api'
import { useSegmentStubs } from '@/features/segment/queries'
import type {
    Destination,
    Distribution,
    DistributionRun,
} from '@/features/segment/types'

// Config state of a run's destination, derived against the live config schema so
// upstream changes (new required field, route flip) resurface automatically.
//   - 'none'         — destination isn't on the marketplace model (no config concept)
//   - 'needs-config' — a route hasn't been chosen, or required fields are unfilled
//   - 'ok'           — fully configured
export interface RowConfigState {
    status: 'none' | 'needs-config' | 'ok'
    // Human-readable labels of what's still outstanding (fields, or "Choose route").
    missing: string[]
}

function rowConfigState(
    platform: string,
    route: DestinationRoute | undefined,
    saved: SavedConfig
): RowConfigState {
    const destination = findDestination(platform)
    if (!destination) return { status: 'none', missing: [] }

    // An unsaved route defaults to the first carrier (see routeForPlatform), so
    // config state turns purely on the connection/setup fields, never on an
    // unmade route choice.
    const resolved = route ?? defaultDestinationRoute(destination)
    const pending = pendingConfig(destination, resolved, saved)
    if (pending.length === 0) return { status: 'ok', missing: [] }
    return {
        status: 'needs-config',
        missing: pending.flatMap((step) => step.missing.map((f) => f.label)),
    }
}

// One row per configured delivery (Distribution): the config + its latest run
// (the summary) + full run history (for drill-down).
export interface DistributionRow {
    id: string
    distribution: Distribution
    latestRun?: DistributionRun
    runs: DistributionRun[]
    threadId: string
    audienceName: string
    segmentName: string
    segmentType: DatasetType
    config: AudienceConfiguration
    // The thread's datasets, for expansion-modeling targets in SegmentDetail.
    datasets: Dataset[]
    destination?: Destination
    // Lazy-config state of this delivery's destination (drives the needs-config notice).
    configState: RowConfigState
}

export interface DistributionsSummary {
    total: number
    live: number
    attention: number
    scheduled: number
}

export interface UseDistributionsInventory {
    rows: DistributionRow[]
    destinations: Destination[]
    summary: DistributionsSummary
    isLoading: boolean
}

// Problems first: failed runs and configs needing setup sort to the top,
// succeeded to the bottom. Within a rank, newest run first.
function rowRank(row: DistributionRow): number {
    if (row.latestRun?.status === 'failed') return 0
    if (row.configState.status === 'needs-config') return 1
    if (row.latestRun?.status === 'partial') return 2
    if (row.latestRun?.status === 'running') return 3
    if (!row.latestRun || row.latestRun.status === 'queued') return 4
    return 5
}

export function useDistributionsInventory(): UseDistributionsInventory {
    const { data: threads, isLoading } = useGetThreads()
    const { lifecycleFor, destinations, savedConfig } = useSegmentStubs()

    const rows = useMemo<DistributionRow[]>(() => {
        if (!threads) return []
        const out: DistributionRow[] = []
        for (const t of threads) {
            const audienceName =
                t.state.threadTitle?.trim() || 'Untitled audience'
            for (const config of t.state.audienceConfigurations ?? []) {
                const key = segmentKey(config)
                const life = lifecycleFor(key, t.threadId)
                for (const delivery of life.deliveries) {
                    const { distribution, latestRun, runs } = delivery
                    const destination = destinations.find(
                        (d) => d.id === distribution.destinationId
                    )
                    out.push({
                        id: distribution.id,
                        distribution,
                        latestRun,
                        runs,
                        threadId: t.threadId,
                        audienceName,
                        segmentName: config.name || 'Untitled segment',
                        segmentType: config.dataset_type,
                        config,
                        datasets: t.state.datasets ?? [],
                        destination,
                        // No destination => it was deleted; the delivery is
                        // orphaned (keeps its runs) and can't deliver.
                        configState: destination
                            ? rowConfigState(
                                  destination.platform,
                                  distribution.route,
                                  savedConfig
                              )
                            : {
                                  status: 'needs-config',
                                  missing: ['Destination removed — reconnect'],
                              },
                    })
                }
            }
        }
        return out.sort((a, b) => {
            const rank = rowRank(a) - rowRank(b)
            if (rank !== 0) return rank
            const at = a.latestRun?.runAt ? Date.parse(a.latestRun.runAt) : 0
            const bt = b.latestRun?.runAt ? Date.parse(b.latestRun.runAt) : 0
            return bt - at
        })
    }, [threads, lifecycleFor, destinations, savedConfig])

    const summary = useMemo<DistributionsSummary>(() => {
        let live = 0
        let attention = 0
        let scheduled = 0
        for (const row of rows) {
            const s = row.latestRun?.status
            // Attention (a failed run OR a destination needing setup) takes
            // precedence over a successful run, so a succeeded-but-needs-config
            // delivery counts once, as attention — matching rowRank's ordering
            // and the page's status filter.
            if (s === 'failed' || row.configState.status === 'needs-config')
                attention++
            else if (s === 'succeeded' || s === 'partial') live++
            else scheduled++
        }
        return { total: rows.length, live, attention, scheduled }
    }, [rows])

    return { rows, destinations, summary, isLoading }
}
